// Countdown state for the "too many attempts" panel shown when Descope reports the SMS
// send channel is rate-limited (see isOtpSendRateLimited in use-descope-web-auth.js).
// Descope doesn't always send a retryAfter for this particular provider-level failure, so
// callers pass DEFAULT_LOCKOUT_SECONDS as a fallback rather than leaving the button
// permanently disabled.
/*
 | Twilio Verify caps the send attempts within a single verification, and a blocked number
 | does NOT clear on a timer of our choosing — it clears when that verification expires,
 | which on Twilio's default is 10 minutes from when it started. A shorter countdown here
 | therefore re-enables the button while the provider is still refusing to send, so the user
 | waits it out, taps "Send a new code" and gets the same failure — observed in testing at
 | the previous 60s value.
 |
 | Descope's own retryAfter is still preferred whenever it sends one (see the callers); this
 | is only the fallback for the provider-level failure where it does not. Erring long is the
 | safe direction: too long merely asks the user to wait a little past the real block, while
 | too short promises a retry that cannot work.
 |
 | VERIFY against the tenant's actual Twilio Verify service TTL before treating 10 minutes as
 | exact — it is configurable on Twilio's side and this constant should mirror it.
 */
export const DEFAULT_LOCKOUT_SECONDS = 600

export default function useOtpSendLockout () {
  const visible = ref(false)
  const secondsRemaining = ref(0)
  // Tracks a retry actually in flight — separate from the countdown, since the button must
  // also stay disabled between the countdown hitting zero and the resend request settling,
  // or a rapid/double click on "Send a new code" fires a second concurrent SMS send.
  const isRetrying = ref(false)
  const canRetry = computed(() => secondsRemaining.value <= 0 && !isRetrying.value)
  let timer = null

  function stopTimer () {
    clearInterval(timer)
    timer = null
  }

  function start (seconds = DEFAULT_LOCKOUT_SECONDS) {
    stopTimer()
    visible.value = true
    secondsRemaining.value = seconds
    timer = setInterval(() => {
      secondsRemaining.value -= 1
      if (secondsRemaining.value <= 0) stopTimer()
    }, 1000)
  }

  function reset () {
    stopTimer()
    visible.value = false
    secondsRemaining.value = 0
    isRetrying.value = false
  }

  // Wraps a retry handler so canRetry (and therefore the panel's button) goes false the
  // instant it's clicked, not just while the countdown is positive — closing the window a
  // second click could land in before the in-flight request settles.
  async function retry (handler) {
    if (!canRetry.value) return
    isRetrying.value = true
    try {
      await handler()
    } finally {
      isRetrying.value = false
    }
  }

  onBeforeUnmount(stopTimer)

  return { visible, secondsRemaining, canRetry, isRetrying, start, reset, retry }
}
